import React, { useEffect, useState } from "react";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridValueFormatterParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import "./styles.scss";
import { ArticleRounded, EditRounded, Search } from "@mui/icons-material";
import { AccountType, GetAccountsResponse } from "../../types/genericType";
import {
  Link,
  TextField,
  Tooltip,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { formatDollar } from "../../utils/utils";
import { SelectChangeEvent } from "@mui/material/Select";

type AccountsTableProps = {
  accountsData: GetAccountsResponse;
  selectedAccount?: AccountType;
  updateSelectedAccount?: Function;
  setOpenEditDrawer?: Function;
  clientView: boolean;
};

const AccountsTable = (props: AccountsTableProps) => {
  const { accountsData, updateSelectedAccount, setOpenEditDrawer, clientView } =
    props;
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRows, setFilteredRows] = useState(accountsData);
  const [seletedStatus, setSelectedStatus] = useState("active");

  const handleSearch = (event: any) => {
    const term = event.target.value;
    setSearchTerm(term);
  };

  useEffect(() => {
    const termParts = searchTerm.trim().toLowerCase().split(/\s+/);
    const filteredData = accountsData.filter((row) => {
      const matchedTerm = termParts.every((term) =>
        Object.values(row).some(
          (value) =>
            typeof value === "string" && value.toLowerCase().includes(term)
        )
      );

      const matchesStatus =
        seletedStatus === "*" ||
        (row.isActive ? "active" : "inactive") === seletedStatus;

      return matchedTerm && matchesStatus;
    });

    setFilteredRows(filteredData);
  }, [accountsData, searchTerm, seletedStatus]);

  const columns: GridColDef[] = React.useMemo<GridColDef[]>(() => {
    const baseColumns: GridColDef[] = [
      {
        field: "name",
        headerName: "Account Name",
        hideable: false,
        minWidth: 200,
        sortable: true,
        renderCell: (params) => (
          <Tooltip title={params.value} arrow>
            <Link
              className="client-cell"
              component={RouterLink}
              sx={{
                textDecoration: "none",
              }}
              to={`${
                clientView
                  ? "/clientTransactions"
                  : "/transactions/transactionHistory"
              }?account=${params.row.accountNumber}&client=${
                params.row.investor
              }`}
            >
              {params.value}
            </Link>
          </Tooltip>
        ),
        headerClassName: "header-datagrid",
        cellClassName: !clientView ? "client-cell" : "",
      },
      {
        field: "accountNumber",
        headerName: "Account#",
        minWidth: 50,
        headerAlign: "right",
        align: "right",
        sortable: false,
        headerClassName: "header-datagrid",
      },
      {
        field: "accountType",
        headerName: "Account Type",
        minWidth: 190,
        sortable: false,
        headerClassName: "header-datagrid",
      },
      {
        field: "interestType",
        headerName: "Interest Type",
        minWidth: 120,
        sortable: false,
        headerClassName: "header-datagrid",
      },
      {
        field: "rate",
        headerName: "Rate",
        type: "number",
        minWidth: 42,
        sortable: false,
        headerClassName: "header-datagrid",
      },
      {
        field: "isActive",
        headerName: "Status",
        minWidth: 57,
        sortable: false,
        headerClassName: "header-datagrid",
        valueGetter: (params: GridValueGetterParams) =>
          `${params.row.isActive ? "Active" : "Inactive"}`,
      },
      {
        field: "balance",
        headerName: "Balance",
        type: "number",
        sortable: false,
        valueFormatter: (params: GridValueFormatterParams<number>) => {
          if (params.value == null) {
            return "";
          }
          return `${formatDollar(params.value)}`;
        },
        minWidth: 120,
        headerClassName: "header-datagrid",
      },
    ];
    if (clientView) {
      baseColumns.push({
        field: "view",
        headerName: "View",
        sortable: false,
        minWidth: 30,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-datagrid",
        type: "actions",
        getActions: (params) => [
          <GridActionsCellItem
            icon={<ArticleRounded className="view-icon" />}
            label=""
            onClick={() => {
              setOpenEditDrawer?.(true);
              updateSelectedAccount?.(params.row);
            }}
          />,
        ],
      });
    }
    if (!clientView) {
      baseColumns.splice(2, 0, {
        field: "investorName",
        headerName: "Client",
        minWidth: 230,
        headerClassName: "header-datagrid",
        sortable: false,
      });
      baseColumns.push({
        field: "edit",
        headerName: "Edit",
        sortable: false,
        minWidth: 30,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-datagrid",
        type: "actions",
        getActions: (params) => [
          <GridActionsCellItem
            icon={<EditRounded sx={{ fontSize: "20px" }} />}
            label=""
            onClick={() => {
              setOpenEditDrawer?.(true);
              updateSelectedAccount?.(params.row);
            }}
          />,
        ],
      });
    }

    return baseColumns;
  }, [clientView, setOpenEditDrawer, updateSelectedAccount]);

  return (
    <div className="data-table">
      {!clientView && (
        <Stack
          spacing={2}
          direction="row"
          className="search-filter"
          padding="20px"
        >
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            className="search-field"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <FormControl style={{ width: 200 }}>
            <InputLabel id="status_label" shrink>
              Status
            </InputLabel>

            <Select
              labelId="status_label"
              label="Status"
              value={seletedStatus}
              onChange={(event: SelectChangeEvent) =>
                setSelectedStatus(event.target.value as string)
              }
              sx={{
                height: "40px", // Adjust this value as needed
                padding: "0 14px", // Adjust this value as needed
                "& .MuiSelect-select": {
                  padding: "0px 0", // Adjust this value as needed
                },
              }}
            >
              <MenuItem value="*">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )}
      {filteredRows.length > 0 ? (
        <DataGrid
          rows={filteredRows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 20, 30, 40, 50]}
          rowSelection={false}
          className="datagrid"
        />
      ) : (
        <div className="no-results">No results found</div>
      )}
    </div>
  );
};

export default AccountsTable;
